import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { BaseSyntheticEvent, FunctionComponent, useMemo } from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';

import { EventType } from '../../../../apollo/__generated__/graphql';
import { usePlacesQuery } from '../../../../place/hooks/usePlacesQuery';

export interface EventFormInputs {
  placeId: string;
  name: string;
  type: EventType;
  description: string;
  checkAddress: string;
  lydiaLink?: string | undefined;
  maxUsers: number;
  startAt: Date;
  endAt: Date;
}

interface EventFormProps {
  register: UseFormRegister<EventFormInputs>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors<EventFormInputs>;
  startAtValue: Date;
  endAtValue: Date;
  onStartAtChange: (date: Date | null) => void;
  onEndAtChange: (date: Date | null) => void;
  onPlaceChange: (placeId: string | null) => void;
  submitButtonLabel: string;
  type: EventType;
  onTypeChange: (type: EventType) => void;
}

export const EventForm: FunctionComponent<EventFormProps> = ({
  register,
  onSubmit,
  errors,
  startAtValue,
  endAtValue,
  onStartAtChange,
  onEndAtChange,
  onPlaceChange,
  submitButtonLabel,
  type,
  onTypeChange,
}) => {
  const { data: placesData } = usePlacesQuery();
  const places = useMemo(() => placesData?.places ?? [], [placesData]);

  return (
    <form onSubmit={onSubmit}>
      <TextField
        margin="normal"
        fullWidth
        id="name"
        label="Nom de l'événement"
        placeholder='Ex: "WET 2024"'
        autoFocus
        type="text"
        {...register('name')}
        helperText={
          errors.name?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.name.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        multiline
        fullWidth
        label="Description"
        type="description"
        id="description"
        {...register('description')}
        helperText={
          errors.description?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.description.message}
            </Typography>
          ) : null
        }
      />

      <RadioGroup
        row
        name="type"
        defaultValue={EventType.BedroomAndTent}
        onChange={(event) => onTypeChange(event.target.value as EventType)}
      >
        <FormControlLabel
          value={EventType.BedroomOnly}
          control={<Radio />}
          label="Chambre uniquement"
        />
        <FormControlLabel
          value={EventType.BedroomAndTent}
          control={<Radio />}
          label="Chambre & tente"
        />
        <FormControlLabel
          value={EventType.TentOnly}
          control={<Radio />}
          label="Tente uniquement"
        />
      </RadioGroup>

      <Autocomplete
        disablePortal
        options={places}
        getOptionLabel={(option) => `${option.name} (${option.address})`}
        noOptionsText="Aucun lieu trouvé"
        sx={{ width: 300, mt: 4 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Chercher un lieu"
            helperText={
              errors.placeId?.message ? (
                <Typography component="p" variant="body2" color="error.main">
                  {errors.placeId.message}
                </Typography>
              ) : null
            }
          />
        )}
        onChange={(_, value) => {
          onPlaceChange(value?.id ?? null);
        }}
      />

      <TextField
        margin="normal"
        multiline
        fullWidth
        label="Adresse pour les chèques (Nom/prénom, rue, code postal, ville)"
        type="checkAddress"
        id="checkAddress"
        {...register('checkAddress')}
        helperText={
          errors.checkAddress?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.checkAddress.message}
            </Typography>
          ) : null
        }
      />
      <TextField
        margin="normal"
        multiline
        fullWidth
        label="Lien vers la Cagnotte Lydia (laisser vide si pas de cagnotte)"
        type="url"
        id="lydiaLink"
        {...register('lydiaLink')}
        helperText={
          errors.lydiaLink?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.lydiaLink.message}
            </Typography>
          ) : null
        }
      />

      {type === EventType.BedroomOnly ? null : (
        <TextField
          margin="normal"
          fullWidth
          label="Nombre de participants maximum"
          type="number"
          id="maxUsers"
          {...register('maxUsers')}
          helperText={
            errors.maxUsers?.message ? (
              <Typography component="p" variant="body2" color="error.main">
                {errors.maxUsers.message}
              </Typography>
            ) : null
          }
        />
      )}

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box>
          <Typography variant="body2">Début</Typography>
          <DateTimePicker
            ampm={false}
            value={startAtValue}
            onChange={onStartAtChange}
          />
          {errors.startAt?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.startAt.message}
            </Typography>
          ) : null}
        </Box>
        <Typography variant="h4" sx={{ mx: 4 }}>
          -
        </Typography>
        <Box>
          <Typography variant="body2">Fin</Typography>
          <DateTimePicker
            ampm={false}
            value={endAtValue}
            onChange={onEndAtChange}
          />
          {errors.endAt?.message ? (
            <Typography component="p" variant="body2" color="error.main">
              {errors.endAt.message}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
        {submitButtonLabel}
      </Button>
    </form>
  );
};
